
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Heart, MessageCircle, Home, MapPin } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { isRenter } from '../utils/roleHelpers';

const RenterBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();

  // Only show for authenticated renters
  if (!user || !profile || !isRenter(profile)) {
    return null;
  }

  const navItems = [
    { 
      icon: Home, 
      label: 'Dashboard', 
      path: '/dashboard',
      gradient: 'from-indigo-500 to-purple-500'
    },
    { 
      icon: Search, 
      label: 'Discover', 
      path: '/discover',
      gradient: 'from-blue-500 to-cyan-500'
    },
    { 
      icon: Heart, 
      label: 'Matches', 
      path: '/matches',
      gradient: 'from-pink-500 to-red-500'
    },
    { 
      icon: MessageCircle, 
      label: 'Messages', 
      path: '/messages',
      gradient: 'from-purple-500 to-pink-500'
    },
    { 
      icon: MapPin, 
      label: 'Map', 
      path: '/map',
      gradient: 'from-emerald-500 to-teal-500'
    }
  ];

  return (
    <>
      {/* Spacer to prevent content overlap - reduced height */}
      <div className="h-16 lg:h-0" />
      
      {/* Bottom Navigation - thinner design */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden h-16">
        {/* Backdrop blur with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/90 to-slate-900/70 backdrop-blur-xl" />
        
        {/* Glass effect border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        
        {/* Navigation content */}
        <div className="relative h-full flex items-center">
          <nav className="flex items-center justify-around w-full px-4 max-w-md mx-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'flex flex-col items-center justify-center transition-all duration-300 group relative',
                    'min-w-[56px] py-1 px-1.5 rounded-xl',
                    isActive ? 'scale-105' : 'hover:scale-105'
                  )}
                >
                  {/* Active background glow */}
                  {isActive && (
                    <>
                      <div className={cn(
                        'absolute inset-0 rounded-xl bg-gradient-to-br opacity-20 blur-sm',
                        item.gradient
                      )} />
                      <div className={cn(
                        'absolute inset-0 rounded-xl bg-gradient-to-br opacity-10',
                        item.gradient
                      )} />
                    </>
                  )}
                  
                  {/* Icon container - smaller for thinner bar */}
                  <div className={cn(
                    'relative z-10 w-9 h-9 rounded-lg flex items-center justify-center mb-0.5',
                    'transition-all duration-300 shadow-md',
                    isActive 
                      ? `bg-gradient-to-br ${item.gradient} shadow-lg` 
                      : 'bg-slate-800/60 border border-slate-700/50 group-hover:bg-slate-700/60 group-hover:border-slate-600/50'
                  )}>
                    <Icon className={cn(
                      'w-4 h-4 transition-all duration-300',
                      isActive 
                        ? 'text-white drop-shadow-sm' 
                        : 'text-slate-400 group-hover:text-slate-200'
                    )} />
                    
                    {/* Active pulse effect */}
                    {isActive && (
                      <div className={cn(
                        'absolute inset-0 rounded-lg bg-gradient-to-br animate-pulse opacity-30',
                        item.gradient
                      )} />
                    )}
                  </div>
                  
                  {/* Label - smaller text for thinner bar */}
                  <span className={cn(
                    'text-[11px] font-medium transition-all duration-300 tracking-wide leading-none',
                    isActive 
                      ? 'text-white font-semibold' 
                      : 'text-slate-400 group-hover:text-slate-200'
                  )}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default RenterBottomNav;
